// src/store/renderControlSlice.ts

import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { set } from "react-hook-form"

type ViewName = "dashboard" | "encounter" | "control" | "statistics"

interface LayerControlState {
  currentView: ViewName
  // 환자 차트 Encounter
  // 환자 신규생성
  showPatientEditLayer: boolean
  // dashboard
  showPatientInfoContainer?: boolean
  showPatientCollapse?: boolean
  showPatientHistory?: boolean
  showPatientDetail?: boolean
  showMemo?: boolean
  showInsurance?: boolean
  showInsuranceG?: boolean
  showBodyPanel?: boolean
  showCurrentChart?: boolean // 현재 차트 정보 표시 여부
  showReservation?: boolean // 예약 정보 표시 여부
  reservationInitialPatientPhone?: string // 미등록 환자 예약 시 초기 전화번호
  reservationWindowActivationKey?: number // 예약 별도창 활성화 요청 키
  // ClaimToHira 레이어 관련
  showClaimToHira?: boolean // ClaimToHira 레이어 표시 여부
  claimToHiraActiveTab?: number // ClaimToHira 레이어의 활성 탭 (0: 일반청구, 1: 추가청구, 2: 보완청구, 3: 치료재료 구입통보)
  claimToHiraWindowActivationKey?: number // ClaimToHira 별도창 활성화 요청 키
  showUninsuredHistoryWindow?: boolean // 비급여/한약 별도창 표시 여부
  uninsuredHistoryWindowActivationKey?: number // 비급여/한약 별도창 활성화 요청 키
}

const initialState: LayerControlState = {
  currentView: "dashboard",
  showPatientEditLayer: false,
  showPatientInfoContainer: false,
  showPatientCollapse: false,
  showPatientHistory: false,
  showPatientDetail: false,
  showMemo: false,
  showInsurance: false, // 서브차트-보험 보이기
  showInsuranceG: false, // 서브차트-지급보증서 보이기
  showBodyPanel: false, // 기본값
  showCurrentChart: false, // 현재 차트 정보 표시 여부
  showReservation: false, // 예약 정보 표시 여부
  reservationInitialPatientPhone: "",
  reservationWindowActivationKey: 0,
  showClaimToHira: false, // ClaimToHira 레이어 표시 여부
  claimToHiraActiveTab: 0, // 기본 탭 (일반청구)
  claimToHiraWindowActivationKey: 0,
  showUninsuredHistoryWindow: false,
  uninsuredHistoryWindowActivationKey: 0,
}

const preserveDetachedWindowState = (state: LayerControlState, nextState: LayerControlState): LayerControlState => ({
  ...nextState,
  showClaimToHira: state.showClaimToHira ?? false,
  claimToHiraActiveTab: state.claimToHiraActiveTab ?? 0,
  claimToHiraWindowActivationKey: state.claimToHiraWindowActivationKey ?? 0,
  showUninsuredHistoryWindow: state.showUninsuredHistoryWindow ?? false,
  uninsuredHistoryWindowActivationKey: state.uninsuredHistoryWindowActivationKey ?? 0,
  showReservation: state.showReservation ?? false,
  reservationInitialPatientPhone: state.reservationInitialPatientPhone ?? "",
  reservationWindowActivationKey: state.reservationWindowActivationKey ?? 0,
})

// 클릭한 환자정보를 보여주는 세트
const detailViewState: LayerControlState = {
  currentView: "dashboard",
  showPatientEditLayer: false,
  showPatientInfoContainer: true,
  showPatientCollapse: true,
  showPatientHistory: true,
  showPatientDetail: true,
  showMemo: true,
  showInsurance: false,
  showInsuranceG: false,
  showBodyPanel: true, // 기본값
  showCurrentChart: true, // 현재 차트 정보 표시 여부
  showReservation: false, // 예약 정보 표시 여부
}

// 대시보드를 클릭했을 때 보여주는 세트
const dashboardViewState: LayerControlState = {
  currentView: "dashboard",
  showPatientEditLayer: false,
  showPatientInfoContainer: true,
  showPatientCollapse: true,
  showPatientHistory: true,
  showPatientDetail: true,
  showMemo: true,
  showInsurance: true,
  showInsuranceG: false,
  showBodyPanel: true,
  showCurrentChart: true,
  showReservation: false, // 예약 정보 표시 여부
}

// 검색결과에서 클릭한 환자정보를 보여주는 세트
const listViewState: LayerControlState = {
  currentView: "dashboard",
  showPatientEditLayer: false,
  showPatientInfoContainer: true,
  showPatientCollapse: true,
  showPatientHistory: true,
  showPatientDetail: true,
  showMemo: true,
  showInsurance: true,
  showInsuranceG: true,
  showBodyPanel: false,
  showCurrentChart: true, // 차팅 전 환자도 빈 상태로 표시
  showReservation: false, // 예약 정보 표시 여부
}

// 환자정보에서 보험을 클릭했을 때 보여주는 세트
const insuranceViewState: LayerControlState = {
  currentView: "dashboard",
  showPatientEditLayer: false,
  showPatientInfoContainer: true,
  showPatientCollapse: true,
  showPatientHistory: true,
  showPatientDetail: true,
  showMemo: true,
  showInsurance: true,
  showInsuranceG: true,
  showBodyPanel: false,
  showCurrentChart: true, // 현재 차트 정보 표시 여부
  showReservation: false, // 예약 정보 표시 여부
}

// 환자차트 Encounter를 보여주는 세트
const encounterViewState: LayerControlState = {
  currentView: "encounter",
  showPatientEditLayer: false,
}

// 환자차트 Encounter를 보여주는 세트
const controlViewState: LayerControlState = {
  currentView: "control",
  showPatientEditLayer: false,
}

const layerControlSlice = createSlice({
  name: "layerControl",
  initialState,
  reducers: {
    // 기존 setLayerControl에 currentLayer도 허용하지만
    // currentLayer가 들어올 때는 반드시 ViewName 여야만 한다고 컴파일 타임에 체크됩니다.
    setLayerControl(state, action: PayloadAction<Partial<LayerControlState>>) {
      Object.assign(state, action.payload)
    },
    // detail 뷰 세트로 **바로 대체**
    setLayerControlDetailView(state) {
      return preserveDetachedWindowState(state, detailViewState)
    },
    // dashboard 뷰 세트로 **바로 대체**
    setLayerControlDashboardView(state) {
      return preserveDetachedWindowState(state, dashboardViewState)
    },
    // list 뷰 세트로 **바로 대체**
    setLayerControlListView(state) {
      return preserveDetachedWindowState(state, listViewState)
    },
    // encounter 뷰 세트로 **바로 대체**
    setLayerControlInsuranceView(state) {
      return preserveDetachedWindowState(state, insuranceViewState)
    },
    // encounter 뷰 세트로 **바로 대체**
    setLayerControlEncounterView(state) {
      return preserveDetachedWindowState(state, encounterViewState)
    },
    // encounter 뷰 세트로 **바로 대체**
    setLayerControlControlView(state) {
      return preserveDetachedWindowState(state, controlViewState)
    },
    // 초기 상태로 완전 복원
    clearLayerControl() {
      return initialState
    },
    // 예약 레이어 보이기
    setReservationView(state, action: PayloadAction<{ patientPhone?: string } | undefined>) {
      // 예약 레이어를 보이도록 상태를 업데이트
      state.showReservation = true
      state.reservationInitialPatientPhone = action.payload?.patientPhone || ""
      state.reservationWindowActivationKey = (state.reservationWindowActivationKey ?? 0) + 1
    },
    // 예약 레이어 숨기기
    clearReservationView(state) {
      state.showReservation = false
      state.reservationInitialPatientPhone = ""
    },
    // ClaimToHira 레이어 열기 (특정 탭으로)
    openClaimToHiraLayer(state, action: PayloadAction<{ activeTab?: number }>) {
      state.showClaimToHira = true
      state.claimToHiraActiveTab = action.payload.activeTab ?? 0
      state.claimToHiraWindowActivationKey = (state.claimToHiraWindowActivationKey ?? 0) + 1
    },
    // ClaimToHira 레이어 닫기
    closeClaimToHiraLayer(state) {
      state.showClaimToHira = false
      state.claimToHiraActiveTab = 0
    },
    openUninsuredHistoryWindow(state) {
      state.showUninsuredHistoryWindow = true
      state.uninsuredHistoryWindowActivationKey = (state.uninsuredHistoryWindowActivationKey ?? 0) + 1
    },
    closeUninsuredHistoryWindow(state) {
      state.showUninsuredHistoryWindow = false
    },
  },
})

export const {
  setLayerControl,
  setLayerControlDetailView,
  setLayerControlDashboardView,
  setLayerControlListView,
  setLayerControlInsuranceView,
  setLayerControlEncounterView,
  setLayerControlControlView,
  clearLayerControl,
  setReservationView,
  clearReservationView,
  openClaimToHiraLayer,
  closeClaimToHiraLayer,
  openUninsuredHistoryWindow,
  closeUninsuredHistoryWindow,
} = layerControlSlice.actions

export default layerControlSlice.reducer

